import React from 'react';
import get from 'lodash/get';
import kebabCase from 'lodash/kebabCase';
import axios from 'axios';

import { Box, ComponentSwitch } from '@origin-digital/layout';
import { Text, Button, AlertBox, LinkText } from '@origin-digital/style-guide';
import { WatchLaterIcon, CheckIcon } from '@origin-digital/icon';
import {
  PlanComparisonCard,
  PriceFactSheetsLink,
  getFuelTypeFromPlan,
} from '@origin-digital/plans';
import { task } from '@origin-digital/reporting-client';

import { IWizardStepProps, WizardContext } from '../../Wizard';
import { RegurgitatedAnswers } from './RegurgitatedAnswers';
import { LoadingText } from './LoadingText';
import { Container, StartOver } from '../common';
import { getRecommendedPlan, getPlanDescription, planTitle } from '../utils';
import { Layout, ReviewHeading, IconPoint, CTAs, ReviewContent, Spaced } from './Review.styles';
import { getContentSourceTitle } from '../../../compare-plans-utils';

interface IReviewState {
  showPostcode?: boolean;
  loading?: boolean;
  fetching?: boolean;
  error?: string;
}

interface IReviewProps extends IWizardStepProps {
  plansApiEndPoint: string;
  addressApiProps: object;
  onComparePlans?: () => any;
  onSelectPlan?: (plan) => any;
  onComplete?: (answers) => any;
}

export class Review extends React.Component<IReviewProps, IReviewState> {
  [x: string]: any;
  static contextType = WizardContext;
  state = {
    loading: true,
    fetching: true,
    showElecRates: false,
    showGasRates: false,
  };

  constructor(props) {
    super(props);
    this.toggleElecRates = this.handleToggleRates.bind(this, 'showElecRates');
    this.toggleGasRates = this.handleToggleRates.bind(this, 'showGasRates');
  }

  componentDidMount() {
    this.fetchPlans();
  }

  fetchPlans() {
    const { plansApiEndPoint, onComplete } = this.props;
    const { address, postcode, usePostcode, electricity, gas, solar } = this.context.answers;
    const fuelType = electricity && gas ? 'dual' : electricity ? 'electricity' : 'gas';
    const params = {
      addressId: address && address.sapAddressId,
      connectionScenario: 'PROS_SWT',
      customerType: 'residential',
      fuelType,
      hasSolarPanels: electricity && solar,
      postcode: postcode || '',
      searchByPostcode: !!usePostcode,
    };

    return axios
      .get(plansApiEndPoint, {
        params,
      })
      .then((response) => {
        const { plans, fuelEligibilityStatus } = response.data.data;
        let recommendedPlan = getRecommendedPlan(plans, this.context.answers);
        if (recommendedPlan) {
          task.base(`wizard-complete-${kebabCase(recommendedPlan.title)}`, {
            action: 'complete',
          });
        } else {
          recommendedPlan = null;
          this.setState({
            error: {
              heading:
                "Sorry, but based on what you've told us, it looks like we don't have any plans available for you right now.",
              message: (
                <>
                  Alternatively, you can{' '}
                  <LinkText text="compare plans" onClick={this.props.onComparePlans} />, get in
                  touch on live chat or try calling us on 13 24 61.
                </>
              ),
            },
          });
        }
        this.context
          .saveAnswers({
            plans,
            fuelEligibilityStatus,
            recommendedPlan,
          })
          .then((answers) => {
            onComplete(answers);
          });
      })
      .catch(() => {
        this.setState({
          error: {
            heading: 'Sorry, an error occurred',
            message:
              "Looks like we're facing issues processing your request at this time. Please try again later or call us on 13 24 61, and we'll be able to help you.",
          },
        });
      })
      .finally(() => {
        this.setState({ fetching: false });
      });
  }

  handleToggleRates(name) {
    this.setState({
      [name]: !this.state[name],
    });
  }

  handleAddress = (address) => {
    this.context.saveAnswers({
      address,
      postcode: undefined,
      usePostcode: false,
    });
  };

  handleSignup = () => {
    const { onSelectPlan } = this.props;
    if (onSelectPlan) {
      onSelectPlan(this.context.answers.recommendedPlan);
    }
  };

  render() {
    const { fetching, loading, showElecRates, showGasRates, error } = this.state;
    const { addressApiProps } = this.props;
    const { answers } = this.context;

    const { recommendedPlan: plan } = answers;
    const callToActions = (
      <Box>
        {!!plan && (
          <Box marginBottom={4}>
            <PriceFactSheetsLink
              fuelType={getFuelTypeFromPlan(plan)}
              region={get(answers, 'fuelEligibility.state')}
              isResidential
              termsAndConditionsUrl={plan.termsAndConditionsUrl}
              links={plan.links}
              addressId={get(answers, 'address.sapAddressId')}
              postcode={answers.postcode}
            />
          </Box>
        )}
        <CTAs>
          <Button
            block
            text="Sign up now"
            onClick={this.handleSignup}
            data-id="originenergy:electricity-gas:plans:button:wizard:sign-up"
          />
          <Button
            block
            kind="secondary"
            text="Compare all plans"
            onClick={this.props.onComparePlans}
          />
          <StartOver {...this.props} />
        </CTAs>
      </Box>
    );

    return (
      <Container>
        <ReviewContent>
          {loading || fetching ? (
            <LoadingText
              text={`
                  Analysing your answers...
                  Fetching rates for your ${answers.usePostcode ? 'postcode' : 'address'}...
                  Unleashing the power...
                  Recommending a plan..
                `}
              onLoaded={() => this.setState({ loading: false })}
            />
          ) : (
            <>
              {!!plan && (
                <Layout>
                  <Spaced>
                    <div>
                      <Box marginBottom={2}>
                        <Text size="md" fontWeight="medium" color="grey56">
                          We recommend
                        </Text>
                      </Box>
                      <ReviewHeading>{plan && planTitle(plan)}</ReviewHeading>
                    </div>
                    <div>
                      <Text size="md" fontWeight="medium" color="grey56">
                        You told us...
                      </Text>
                      <RegurgitatedAnswers answers={answers} />
                    </div>
                    <div>
                      <Box marginBottom={4}>
                        <Text size="md" fontWeight="medium" color="grey56">
                          Why we chose {plan && planTitle(plan)} for you
                        </Text>
                      </Box>
                      <Text>{getPlanDescription(plan, answers)}</Text>
                    </div>
                    <div>
                      <Box marginBottom={4}>
                        <Text size="md" fontWeight="medium" color="grey56">
                          Let's get it in the bag
                        </Text>
                      </Box>
                      <IconPoint>
                        <WatchLaterIcon size={32} />
                        <Text>Only takes 5 minutes of your time</Text>
                      </IconPoint>
                      <IconPoint>
                        <CheckIcon size={32} />
                        <Text>No exit fees or lock in contracts</Text>
                      </IconPoint>
                    </div>
                    <ComponentSwitch
                      breakpoint="MD"
                      showLessThan={null}
                      showGreaterOrEqualTo={callToActions}
                    />
                  </Spaced>
                  <div>
                    <PlanComparisonCard
                      plan={plan}
                      renewableEnergy={{
                        electricity: answers.greenElectricity,
                        gas: answers.greenGas,
                      }}
                      contentSourceTitle={getContentSourceTitle(plan.title)}
                      address={get(answers.address, 'qasSearchResult.label')}
                      addressApiProps={addressApiProps}
                      onAddressChange={this.handleAddress}
                      onElecAccordionToggle={this.toggleElecRates}
                      onGasAccordionToggle={this.toggleGasRates}
                      showElecRates={showElecRates}
                      showGasRates={showGasRates}
                      searchByPostcode={answers.usePostcode}
                      customerType="residential"
                    />
                    <Box marginTop={8}>
                      <ComponentSwitch
                        breakpoint="MD"
                        showGreaterOrEqualTo={null}
                        showLessThan={callToActions}
                      />
                    </Box>
                  </div>
                </Layout>
              )}
              {!!error && <AlertBox type="error" heading={error.heading} message={error.message} />}
            </>
          )}
        </ReviewContent>
      </Container>
    );
  }
}
